import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Loader2, CheckCircle, Clock, Star, BookOpen, Download, Search, Shield, Smartphone } from "lucide-react";
import Navigation from "../components/Navigation";
import { Button } from "@/components/ui/button";
import MobileMoneyDialog from "../components/MobileMoneyDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_PLANS = [
  { id: "1m", label: "1 Month", desc: "Perfect to try The Archive for a short period.", billing: "Billed once every month" },
  { id: "3m", label: "3 Months", desc: "Good for short research projects and courses.", billing: "Billed once every 3 months" },
  { id: "6m", label: "6 Months", desc: "Best for longer projects and extended research.", billing: "Billed once every 6 months" },
  { id: "12m", label: "12 Months", desc: "Best value for ongoing research and frequent use.", billing: "Billed once every 12 months", best: true },
];

const FEATURES = [
  { icon: Download, title: "Unlimited Downloads", desc: "Download any book or file without monthly limits." },
  { icon: BookOpen, title: "Full Library Access", desc: "Access the entire collection of books, articles, and research papers." },
  { icon: Search, title: "Advanced Search", desc: "Use smart filters to find exactly what you need by date, type, language, and more." },
  { icon: Shield, title: "Priority Support", desc: "Get help faster when you need it." },
];

const SubscriptionsPage = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState({});
  const [trialDays, setTrialDays] = useState(5);
  const [freeLimit, setFreeLimit] = useState(10);
  const [processingPlan, setProcessingPlan] = useState("");
  const [billingMessage, setBillingMessage] = useState("");
  const [mmOpen, setMmOpen] = useState(false);
  const [mmPlan, setMmPlan] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const billingSuccess = params.get("billing_success");
    const sessionId = params.get("session_id");
    const billingCancelled = params.get("billing_cancelled");

    const fetchData = async () => {
      try {
        const [settingsResp] = await Promise.all([
          axios.get(`${API}/platform-info`).catch(() => ({ data: {} })),
        ]);
        setPlans(settingsResp.data?.subscription_plans || {});
        setTrialDays(settingsResp.data?.trial_days || 5);
        setFreeLimit(settingsResp.data?.free_download_limit || 10);

        // Fetch membership status only if logged in
        const token = localStorage.getItem("access_token");
        if (token) {
          const statusResp = await axios.get(`${API}/auth/membership-status`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setStatus(statusResp.data);
        }
      } catch (err) {
        console.error("Status error:", err);
      } finally {
        setLoading(false);
      }
    };

    const handleBillingReturn = async () => {
      if (billingSuccess === "true" && sessionId) {
        try {
          const token = localStorage.getItem("access_token");
          const resp = await axios.get(`${API}/billing/subscription-status`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { session_id: sessionId },
          });
          setBillingMessage(
            resp.data?.payment_status === "paid"
              ? "Subscription activated successfully!"
              : "Subscription status: " + (resp.data?.payment_status || "unknown")
          );
        } catch {
          setBillingMessage("Error verifying subscription payment.");
        } finally {
          window.history.replaceState({}, "", window.location.pathname);
        }
      } else if (billingCancelled === "true") {
        setBillingMessage("Subscription checkout was cancelled.");
        window.history.replaceState({}, "", window.location.pathname);
      }
    };

    handleBillingReturn();
    fetchData();
  }, []);

  const startCheckout = async (plan) => {
    if (!user) {
      navigate("/login");
      return;
    }
    setProcessingPlan(plan);
    try {
      const token = localStorage.getItem("access_token");
      const formData = new FormData();
      formData.append("plan", plan);
      formData.append("origin", window.location.origin);
      const resp = await axios.post(`${API}/billing/create-subscription-checkout`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.data?.checkout_url) {
        window.location.href = resp.data.checkout_url;
      } else {
        setBillingMessage("Unable to start subscription checkout.");
      }
    } catch (err) {
      setBillingMessage(err.response?.data?.detail || "Error starting subscription checkout.");
    } finally {
      setProcessingPlan("");
    }
  };

  const getPrice = (planId) => {
    const p = plans[planId];
    return p ? p.price : null;
  };

  const subStatus = status?.subscription_status || "none";
  const hasActiveAccess = !!status?.has_active_access;

  return (
    <div className="min-h-screen bg-sky-50">
      <Navigation user={user} setUser={setUser} />

      <div className="max-w-5xl mx-auto px-4 py-10">
        {/* Hero */}
        <div className="text-center mb-10">
          <h1
            className="text-4xl sm:text-5xl font-bold text-slate-900 mb-3"
            style={{ fontFamily: "Fraunces, serif" }}
            data-testid="subscriptions-title"
          >
            Choose Your Plan
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Get unlimited access to The Archive's entire library of books, articles, and research files.
            Start with a free {trialDays}-day trial, then pick the plan that works for you.
          </p>
        </div>

        <section className="max-w-3xl mx-auto mb-10 text-slate-600 text-sm leading-relaxed" data-testid="subscriptions-intro">
          <p className="mb-3">
            The Archive is a curated digital library of over 8,000 scientific and technical documents — books, peer-reviewed articles, theses, and research reports covering <strong>extractive metallurgy</strong>, <strong>mining engineering</strong>, <strong>hydrometallurgy</strong>, <strong>cobalt and copper processing</strong>, <strong>industrial chemistry</strong>, and applied scientific research.
            Every plan includes full-text search inside every PDF, AI-generated abstracts, on-demand translation across 10 languages, and unlimited downloads for the duration of your subscription.
            Free-tier members get 10 downloads per month; paid plans unlock the entire library and priority support.
          </p>
          <p className="mb-3">
            Whether you are preparing a literature review for a metallurgical thesis, sourcing operating data for a hydrometallurgical circuit, benchmarking flotation performance for a plant audit, or building a reading list for a graduate course, a paid subscription gives you full access to the library without per-download friction.
            Subscriptions are billed in advance via Stripe, are renewable at any time, and can be cancelled from your profile — access continues until the end of the current paid period.
          </p>
          <p className="text-slate-500 text-xs">
            <strong>What you get on every paid plan:</strong> unlimited downloads · full-text search inside every PDF · AI-generated abstracts and multilingual translations · reading collections (public and private) · priority email support · thumbnail previews · citation-ready metadata · plagiarism / similarity checking · early access to new features.
          </p>
        </section>

        {billingMessage && (
          <div
            className={`mb-6 p-3 rounded-md border text-sm text-center ${
              billingMessage.includes("success")
                ? "border-green-200 bg-green-50 text-green-700"
                : "border-slate-200 bg-slate-50 text-slate-700"
            }`}
            data-testid="billing-message"
          >
            {billingMessage}
          </div>
        )}

        {/* Current status (logged-in users only) */}
        {user && status && (
          <div className="mb-8 p-4 bg-white border border-slate-200 rounded-lg max-w-md mx-auto" data-testid="subscription-status">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Your current status</div>
            <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
              {hasActiveAccess && <CheckCircle className="w-4 h-4 text-green-500" />}
              {subStatus === "active" && <span>Active subscription</span>}
              {subStatus === "trial" && (
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4 text-orange-500" />
                  Trial period
                </span>
              )}
              {subStatus === "none" && <span>Free tier ({freeLimit} downloads/month)</span>}
            </div>
            {status?.trial_end_date && subStatus === "trial" && (
              <div className="text-xs text-slate-500 mt-1">
                Trial ends on {new Date(status.trial_end_date).toLocaleDateString()}
              </div>
            )}
            {status?.subscription_end_date && subStatus === "active" && (
              <div className="text-xs text-slate-500 mt-1">
                Subscription ends on {new Date(status.subscription_end_date).toLocaleDateString()}
              </div>
            )}
          </div>
        )}

        {/* What's included */}
        <div className="mb-10">
          <h2 className="text-center text-lg font-semibold text-slate-800 mb-5" style={{ fontFamily: "Fraunces, serif" }}>
            What's included with every plan
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-white border border-slate-200 rounded-lg p-4 text-center">
                <f.icon className="w-6 h-6 text-orange-500 mx-auto mb-2" />
                <h3 className="text-sm font-semibold text-slate-900 mb-1">{f.title}</h3>
                <p className="text-xs text-slate-500">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Free vs Paid comparison */}
        <div className="mb-10 bg-white border border-slate-200 rounded-lg overflow-hidden max-w-2xl mx-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-slate-600 font-medium">Feature</th>
                <th className="px-4 py-3 text-center text-slate-600 font-medium">Free</th>
                <th className="px-4 py-3 text-center text-orange-600 font-medium">Subscribed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <tr>
                <td className="px-4 py-3 text-slate-700">Monthly downloads</td>
                <td className="px-4 py-3 text-center text-slate-500">{freeLimit}</td>
                <td className="px-4 py-3 text-center font-medium text-slate-900">Unlimited</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-slate-700">Library access</td>
                <td className="px-4 py-3 text-center text-slate-500">Limited</td>
                <td className="px-4 py-3 text-center font-medium text-slate-900">Full</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-slate-700">Advanced search & filters</td>
                <td className="px-4 py-3 text-center text-slate-500">Basic</td>
                <td className="px-4 py-3 text-center font-medium text-slate-900">Full</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-slate-700">Reading collections</td>
                <td className="px-4 py-3 text-center text-slate-400">-</td>
                <td className="px-4 py-3 text-center"><CheckCircle className="w-4 h-4 text-green-500 mx-auto" /></td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-slate-700">Free trial</td>
                <td className="px-4 py-3 text-center text-slate-400">-</td>
                <td className="px-4 py-3 text-center font-medium text-slate-900">{trialDays} days</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Plan cards */}
        {loading ? (
          <div className="flex items-center justify-center gap-2 text-sm text-slate-600 py-8">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading plans...
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="subscription-plans">
            {DEFAULT_PLANS.map((plan) => {
              const price = getPrice(plan.id);
              return (
                <div
                  key={plan.id}
                  className={`bg-white rounded-lg p-5 flex flex-col justify-between transition-all ${
                    plan.best ? "border-2 border-orange-400 shadow-md" : "border border-slate-200"
                  }`}
                  data-testid={`plan-${plan.id}`}
                >
                  <div>
                    {plan.best && (
                      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-[11px] font-medium mb-2">
                        <Star className="w-3 h-3" />
                        Best value
                      </div>
                    )}
                    <h2 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: "Fraunces, serif" }}>
                      {plan.label}
                    </h2>
                    <p className="text-xs text-slate-500 mb-3">{plan.desc}</p>
                    <div className="text-3xl font-bold text-slate-900 mb-1">
                      {price !== null ? `$${price}` : "-"}
                    </div>
                    <div className="text-xs text-slate-500 mb-4">{plan.billing}</div>
                  </div>
                  <Button
                    onClick={() => startCheckout(plan.id)}
                    disabled={!!processingPlan}
                    className={`w-full mt-2 ${
                      plan.best
                        ? "bg-orange-500 hover:bg-orange-600 text-white"
                        : "bg-slate-900 hover:bg-slate-800 text-white"
                    }`}
                    data-testid={`subscribe-${plan.id}`}
                  >
                    {processingPlan === plan.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Redirecting...
                      </>
                    ) : user ? (
                      `Subscribe for ${plan.label.toLowerCase()}`
                    ) : (
                      `Sign up - ${plan.label.toLowerCase()}`
                    )}
                  </Button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!user) {
                        navigate("/login");
                        return;
                      }
                      setMmPlan(plan.id);
                      setMmOpen(true);
                    }}
                    className="w-full mt-2 text-xs text-slate-600 hover:text-orange-700 border border-slate-300 hover:border-orange-400 rounded-full py-2 flex items-center justify-center gap-2 transition-colors"
                    data-testid={`mobile-money-${plan.id}`}
                  >
                    <Smartphone className="w-3.5 h-3.5" />
                    Pay with Mobile Money
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* FAQ */}
        <div className="max-w-2xl mx-auto mb-10">
          <h2 className="text-lg font-semibold text-slate-800 mb-4 text-center" style={{ fontFamily: "Fraunces, serif" }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {[
              { q: "Can I cancel my subscription?", a: "Yes, you can cancel anytime. Your access continues until the end of your billing period." },
              { q: "Is there a free trial?", a: `Yes! New users get a ${trialDays}-day free trial with full access to the library. No payment required to start.` },
              { q: "What happens when my subscription expires?", a: `You return to the free tier with ${freeLimit} downloads per month. Your collections and account data are preserved.` },
              { q: "Can I switch plans?", a: "Yes, you can upgrade to a longer plan at any time. The new plan starts immediately after payment." },
              { q: "What payment methods do you accept?", a: "We accept all major credit and debit cards through Stripe, our secure payment processor." },
            ].map((item) => (
              <details key={item.q} className="bg-white border border-slate-200 rounded-lg group">
                <summary className="px-4 py-3 cursor-pointer text-sm font-medium text-slate-800 hover:text-orange-600">
                  {item.q}
                </summary>
                <p className="px-4 pb-3 text-sm text-slate-600">{item.a}</p>
              </details>
            ))}
          </div>
        </div>

        {/* CTA for non-logged-in users */}
        {!user && (
          <div className="text-center py-6 bg-white border border-slate-200 rounded-lg">
            <p className="text-slate-700 font-medium mb-2">Ready to get started?</p>
            <p className="text-sm text-slate-500 mb-4">Create a free account and get {trialDays} days of full access.</p>
            <Button
              onClick={() => navigate("/register")}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="cta-register-btn"
            >
              Create Free Account
            </Button>
          </div>
        )}
      </div>

      <MobileMoneyDialog
        open={mmOpen}
        onClose={() => setMmOpen(false)}
        onSuccess={() => { window.location.reload(); }}
        purpose="subscription"
        plan={mmPlan}
        itemTitle={mmPlan ? `Subscription — ${mmPlan}` : null}
      />
    </div>
  );
};

export default SubscriptionsPage;
